import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, GraduationCap, Loader2, Play, Plus, Sparkles, Trash2 } from "lucide-react";
import { Page } from "@/components/layout";
import { ContentCard } from "@/components/ui/ContentCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { deriveWorkspacePersona } from "@/config/workspacePersona";
import {
  getTeacherScenariosForPersona,
  TEACHER_SCENARIO_CATEGORIES,
  type TeacherScenario,
  type TeacherScenarioCategory,
  type TeacherScenarioMode,
} from "@/config/teacherScenarios";
import {
  customScenariosChangedEvent,
  deleteCustomScenario,
  listCustomScenarios,
  saveCustomScenario,
  type CustomScenarioInput,
} from "@/services/customScenarioService";
import { ScenarioChatPanel } from "./teacher-scenarios/ScenarioChatPanel";
import { ScenarioBriefCard } from "./teacher-scenarios/ScenarioBriefCard";
import { ScenarioBuilderDialog } from "./teacher-scenarios/ScenarioBuilderDialog";
import { SCENARIO_PAGE_SHELL } from "./teacher-scenarios/scenarioPageLayout";
import { chatHistoryService } from "@/services/chatHistoryService";
import { sessionService } from "@/services/sessionService";
import {
  parseScenarioSessionFromHistory,
  resolveScenarioById,
} from "@/services/teacherScenarioService";
import { isScenarioResumeState } from "@/types/scenarioNavigation";

type PagePhase = "browse" | "configure" | "session";
type CategoryFilter = TeacherScenarioCategory | "all" | "my";

const MODE_OPTIONS: {
  id: TeacherScenarioMode;
  label: string;
  description: string;
  icon: typeof Sparkles;
}[] = [
  {
    id: "prep",
    label: "Prep",
    description: "Anticipate the moment, sharpen language, and build regulation strategies.",
    icon: Sparkles,
  },
  {
    id: "practice",
    label: "Practice",
    description: "Describe your plan, then role-play with coaching and mid-scenario twists.",
    icon: Play,
  },
];

function ScenarioPageHeader({
  phase,
  title,
  subtitle,
  onBack,
  backLabel,
}: {
  phase: PagePhase;
  title: string;
  subtitle: string;
  onBack?: () => void;
  backLabel?: string;
}) {
  return (
    <Page.Header className="space-y-0 pb-2 md:pb-2 md:pt-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          {phase === "browse" && (
            <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-white/15">
              <GraduationCap className="h-5 w-5 text-white" />
            </div>
          )}
          {phase !== "browse" && (
            <p className="font-inter text-xs font-semibold uppercase tracking-wide text-white/70">
              {phase === "configure" ? "Step 2 of 2" : "Coaching session"}
            </p>
          )}
          <h1 className="font-literata text-xl font-semibold text-white md:text-2xl">{title}</h1>
          <p className="mt-0.5 max-w-3xl font-inter text-xs text-white/80 md:text-sm">{subtitle}</p>
        </div>
        {onBack && backLabel && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="shrink-0 text-white/80 hover:bg-white/10 hover:text-white"
          >
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            {backLabel}
          </Button>
        )}
      </div>
    </Page.Header>
  );
}

export const TeacherScenariosPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userData } = useAuth();
  const persona = deriveWorkspacePersona(userData);
  const userId = userData?.user_id;
  const builtInScenarios = useMemo(() => getTeacherScenariosForPersona(persona), [persona]);
  const contentRef = useRef<HTMLDivElement>(null);
  const resumeHandledRef = useRef<string | null>(null);
  const activeSessionIdRef = useRef<string | null>(null);

  const incomingResumeSessionId =
    (isScenarioResumeState(location.state) ? location.state.resumeSessionId : null) ??
    searchParams.get("session") ??
    undefined;

  const [phase, setPhase] = useState<PagePhase>("browse");
  const [activeCategory, setActiveCategory] = useState<CategoryFilter>("all");
  const [selectedScenario, setSelectedScenario] = useState<TeacherScenario | null>(null);
  const [selectedMode, setSelectedMode] = useState<TeacherScenarioMode | null>(null);
  const [sessionScenario, setSessionScenario] = useState<TeacherScenario | null>(null);
  const [sessionMode, setSessionMode] = useState<TeacherScenarioMode | null>(null);
  const [resumeSessionIdActive, setResumeSessionIdActive] = useState<string | undefined>(undefined);
  const [isResuming, setIsResuming] = useState(Boolean(incomingResumeSessionId));
  const [resumeError, setResumeError] = useState<string | null>(null);
  const [customScenarios, setCustomScenarios] = useState<TeacherScenario[]>([]);
  const [customScenariosReady, setCustomScenariosReady] = useState(false);
  const [builderOpen, setBuilderOpen] = useState(false);

  const clearResumeNavigation = useCallback(() => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("session");
    const search = nextParams.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : "" },
      { replace: true, state: null },
    );
  }, [location.pathname, navigate, searchParams]);

  useEffect(() => {
    setCustomScenarios(listCustomScenarios(userId));
    setCustomScenariosReady(true);
    const refresh = () => setCustomScenarios(listCustomScenarios(userId));
    window.addEventListener(customScenariosChangedEvent, refresh);
    return () => window.removeEventListener(customScenariosChangedEvent, refresh);
  }, [userId]);

  const allScenarios = useMemo(
    () => [...customScenarios, ...builtInScenarios],
    [customScenarios, builtInScenarios],
  );

  const filteredScenarios = useMemo(() => {
    if (activeCategory === "all") return allScenarios;
    if (activeCategory === "my") return customScenarios;
    return allScenarios.filter((s) => s.category === activeCategory);
  }, [activeCategory, allScenarios, customScenarios]);

  const visibleCategories = useMemo(() => {
    const ids = new Set(allScenarios.map((s) => s.category));
    return TEACHER_SCENARIO_CATEGORIES.filter((c) => ids.has(c.id));
  }, [allScenarios]);

  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [phase, selectedScenario?.id]);

  useEffect(() => {
    if (!incomingResumeSessionId) {
      setIsResuming(false);
      return;
    }

    if (resumeHandledRef.current === incomingResumeSessionId) {
      setIsResuming(false);
      return;
    }

    if (!customScenariosReady) {
      setIsResuming(true);
      return;
    }

    let cancelled = false;

    void (async () => {
      setIsResuming(true);
      setResumeError(null);

      try {
        const history = await chatHistoryService.getChatHistoryBySessionId(incomingResumeSessionId);
        const parsed = parseScenarioSessionFromHistory(history);
        if (!parsed) {
          if (!cancelled) {
            resumeHandledRef.current = incomingResumeSessionId;
            setResumeError("Could not restore this scenario session.");
            clearResumeNavigation();
          }
          return;
        }

        const scenario = resolveScenarioById(parsed.scenarioId, allScenarios);
        if (!scenario) {
          if (!cancelled) {
            resumeHandledRef.current = incomingResumeSessionId;
            setResumeError("This scenario is no longer available in your library.");
            clearResumeNavigation();
          }
          return;
        }

        if (cancelled) return;

        resumeHandledRef.current = incomingResumeSessionId;
        setSelectedScenario(scenario);
        setSelectedMode(parsed.mode);
        setSessionScenario(scenario);
        setSessionMode(parsed.mode);
        setResumeSessionIdActive(incomingResumeSessionId);
        activeSessionIdRef.current = incomingResumeSessionId;
        setPhase("session");
        clearResumeNavigation();
      } catch {
        if (!cancelled) {
          resumeHandledRef.current = incomingResumeSessionId;
          setResumeError("Could not open this scenario session.");
          clearResumeNavigation();
        }
      } finally {
        if (!cancelled) setIsResuming(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [allScenarios, clearResumeNavigation, customScenariosReady, incomingResumeSessionId]);

  useEffect(() => {
    if (phase === "session") return;

    const sessionId = activeSessionIdRef.current;
    activeSessionIdRef.current = null;
    if (!sessionId) return;

    void sessionService.closeSession(sessionId);
  }, [phase]);

  const handleSelectScenario = (scenario: TeacherScenario) => {
    setSelectedScenario(scenario);
    setSelectedMode(null);
    setPhase("configure");
  };

  const handleBackToBrowse = () => {
    setPhase("browse");
    setSelectedScenario(null);
    setSelectedMode(null);
    setSessionScenario(null);
    setSessionMode(null);
    setResumeSessionIdActive(undefined);
    setResumeError(null);
    clearResumeNavigation();
  };

  const handleBackToConfigure = () => {
    if (sessionScenario) {
      setSelectedScenario(sessionScenario);
      setSelectedMode(sessionMode);
    }
    setSessionScenario(null);
    setSessionMode(null);
    setResumeSessionIdActive(undefined);
    setPhase("configure");
  };

  const handleSessionActive = useCallback((sessionId: string | null) => {
    activeSessionIdRef.current = sessionId;
  }, []);

  const handleStartSession = () => {
    if (!selectedScenario || !selectedMode) return;
    setSessionScenario(selectedScenario);
    setSessionMode(selectedMode);
    setResumeSessionIdActive(undefined);
    activeSessionIdRef.current = null;
    setPhase("session");
  };

  const handleCreateScenario = useCallback(
    (input: CustomScenarioInput) => {
      if (!userId) return;
      const created = saveCustomScenario(userId, input);
      setCustomScenarios(listCustomScenarios(userId));
      setActiveCategory("my");
      setSelectedScenario(created);
      setSelectedMode(null);
      setPhase("configure");
    },
    [userId],
  );

  const handleDeleteScenario = useCallback(
    (e: React.MouseEvent, id: string) => {
      e.stopPropagation();
      if (!userId) return;
      deleteCustomScenario(userId, id);
      setCustomScenarios(listCustomScenarios(userId));
    },
    [userId],
  );

  const builderDefaultCategory: TeacherScenarioCategory =
    activeCategory === "all" || activeCategory === "my" ? "student_behavior" : activeCategory;

  const headerTitle =
    phase === "browse"
      ? "Scenario Practice"
      : phase === "configure"
        ? (selectedScenario?.title ?? "Scenario Practice")
        : (sessionScenario?.title ?? "Scenario Practice");

  const headerSubtitle =
    phase === "browse"
      ? "Practice hard moments so you feel ready, build something to share with admin, and keep a resource for yourself."
      : phase === "configure"
        ? `${TEACHER_SCENARIO_CATEGORIES.find((c) => c.id === selectedScenario?.category)?.label ?? "Scenario"} · Choose Prep or Practice, then start coaching`
        : `${sessionMode === "prep" ? "Prep" : "Practice"} mode · Work through your plan with HeyBodhi coaching`;

  return (
    <Page.Container>
      <ScenarioPageHeader
        phase={phase}
        title={headerTitle}
        subtitle={headerSubtitle}
        onBack={
          phase === "configure"
            ? handleBackToBrowse
            : phase === "session"
              ? handleBackToConfigure
              : undefined
        }
        backLabel={phase === "configure" ? "Change scenario" : phase === "session" ? "Setup & mode" : undefined}
      />

      <Page.Content
        ref={contentRef}
        hideFooter={phase === "session"}
        className={cn(
          phase === "session" ? SCENARIO_PAGE_SHELL.contentSession : SCENARIO_PAGE_SHELL.content,
          phase === "session" && "overflow-hidden space-y-0",
        )}
      >
        {isResuming && (
          <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-white" aria-label="Loading session" />
            <p className="font-inter text-sm text-white/80">Opening your scenario session…</p>
          </div>
        )}

        {!isResuming && resumeError && (
          <ContentCard className={cn(SCENARIO_PAGE_SHELL.card, "text-center")}>
            <p className="font-inter text-sm text-gray-700">{resumeError}</p>
            <Button type="button" variant="outline" className="mt-4" onClick={handleBackToBrowse}>
              Back to scenarios
            </Button>
          </ContentCard>
        )}

        {!isResuming && !resumeError && phase === "session" && sessionScenario && sessionMode && (
          <ScenarioChatPanel
            key={`${sessionScenario.id}-${sessionMode}-${resumeSessionIdActive ?? "new"}`}
            scenario={sessionScenario}
            initialMode={sessionMode}
            resumeSessionId={resumeSessionIdActive}
            onBackToConfigure={handleBackToConfigure}
            onSessionActive={handleSessionActive}
          />
        )}

        {!isResuming && !resumeError && phase === "configure" && selectedScenario && (
          <>
            <ScenarioBriefCard scenario={selectedScenario} mode={selectedMode} collapsible={false} />

            <ContentCard className={SCENARIO_PAGE_SHELL.card}>
              <p className="font-inter text-xs font-semibold uppercase tracking-wide text-gray-500">Choose your mode</p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                {MODE_OPTIONS.map((option) => {
                  const Icon = option.icon;
                  const isActive = selectedMode === option.id;

                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setSelectedMode(option.id)}
                      className={cn(
                        "rounded-xl border-2 p-4 text-left transition-all",
                        isActive
                          ? "border-bodhi-teal bg-bodhi-teal/5 ring-2 ring-bodhi-teal/20"
                          : "border-gray-200 bg-white hover:border-bodhi-teal/40",
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <Icon className={cn("h-4 w-4", isActive ? "text-bodhi-teal" : "text-gray-500")} />
                        <span className="font-literata text-base font-semibold text-bodhi-teal">{option.label}</span>
                      </div>
                      <p className="mt-2 font-inter text-sm leading-relaxed text-gray-700">{option.description}</p>
                    </button>
                  );
                })}
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-gray-100 pt-4">
                <Button
                  type="button"
                  size="lg"
                  disabled={!selectedMode}
                  onClick={handleStartSession}
                  className="bg-bodhi-teal font-inter hover:bg-bodhi-teal/90"
                >
                  <Play className="mr-2 h-4 w-4" />
                  Start coaching
                </Button>
                {!selectedMode ? (
                  <p className="font-inter text-sm text-gray-500">Select Prep or Practice to begin.</p>
                ) : (
                  <p className="font-inter text-sm text-gray-600">
                    HeyBodhi will coach you using frameworks matched to this scenario.
                  </p>
                )}
              </div>
            </ContentCard>
          </>
        )}

        {!isResuming && !resumeError && phase === "browse" && (
          <>
            <ContentCard className={SCENARIO_PAGE_SHELL.card}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="font-inter text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Filter by category
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={!userId}
                  onClick={() => setBuilderOpen(true)}
                  className="border-bodhi-teal/30 font-inter text-bodhi-teal hover:bg-bodhi-teal/5"
                >
                  <Plus className="mr-1.5 h-4 w-4" />
                  Create your own
                </Button>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setActiveCategory("all")}
                  className={cn(
                    "rounded-full px-3 py-1.5 font-inter text-sm font-medium transition-colors",
                    activeCategory === "all"
                      ? "bg-bodhi-teal text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200",
                  )}
                >
                  All
                </button>
                {visibleCategories.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setActiveCategory(cat.id)}
                    className={cn(
                      "rounded-full px-3 py-1.5 font-inter text-sm font-medium transition-colors",
                      activeCategory === cat.id
                        ? "bg-bodhi-teal text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200",
                    )}
                  >
                    {cat.label}
                  </button>
                ))}
                {customScenarios.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setActiveCategory("my")}
                    className={cn(
                      "rounded-full px-3 py-1.5 font-inter text-sm font-medium transition-colors",
                      activeCategory === "my"
                        ? "bg-bodhi-teal text-white"
                        : "bg-bodhi-gold/15 text-bodhi-teal hover:bg-bodhi-gold/25",
                    )}
                  >
                    My Scenarios ({customScenarios.length})
                  </button>
                )}
              </div>
            </ContentCard>

            {activeCategory === "my" && customScenarios.length === 0 ? (
              <ContentCard className={cn(SCENARIO_PAGE_SHELL.card, "text-center")}>
                <p className="font-inter text-sm text-gray-600">
                  You haven&apos;t created any scenarios yet. Use{" "}
                  <span className="font-medium text-bodhi-teal">Create your own</span> to practice a real situation.
                </p>
              </ContentCard>
            ) : filteredScenarios.length === 0 ? (
              <ContentCard className={cn(SCENARIO_PAGE_SHELL.card, "text-center")}>
                <p className="font-inter text-sm text-gray-600">No scenarios match this filter for your workspace role.</p>
              </ContentCard>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {filteredScenarios.map((scenario) => {
                  const categoryMeta = TEACHER_SCENARIO_CATEGORIES.find((c) => c.id === scenario.category);

                  return (
                    <button
                      key={scenario.id}
                      type="button"
                      onClick={() => handleSelectScenario(scenario)}
                      className="text-left"
                    >
                      <ContentCard
                        className={cn(
                          "flex h-full flex-col transition-all hover:border-bodhi-teal/40",
                          SCENARIO_PAGE_SHELL.card,
                        )}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-inter text-xs font-semibold uppercase tracking-widest text-bodhi-teal/80">
                            {categoryMeta?.label}
                          </p>
                          {scenario.isCustom && (
                            <Badge className="shrink-0 border-bodhi-gold/40 bg-bodhi-gold/15 font-inter text-[10px] text-bodhi-teal hover:bg-bodhi-gold/15">
                              Custom
                            </Badge>
                          )}
                        </div>
                        <h3 className="mt-1 font-literata text-lg font-semibold text-bodhi-teal">{scenario.title}</h3>
                        <p className="mt-2 flex-1 font-inter text-sm leading-relaxed text-gray-700">{scenario.summary}</p>
                        <div className="mt-3 flex items-center justify-between">
                          <p className="font-inter text-xs font-medium text-bodhi-teal">Select scenario →</p>
                          {scenario.isCustom && (
                            <span
                              role="button"
                              tabIndex={0}
                              aria-label="Delete scenario"
                              onClick={(e) => handleDeleteScenario(e, scenario.id)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  handleDeleteScenario(e as unknown as React.MouseEvent, scenario.id);
                                }
                              }}
                              className="inline-flex items-center gap-1 rounded-md px-2 py-1 font-inter text-xs text-gray-400 transition hover:bg-red-50 hover:text-red-600"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              Delete
                            </span>
                          )}
                        </div>
                      </ContentCard>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}
      </Page.Content>

      <ScenarioBuilderDialog
        open={builderOpen}
        onOpenChange={setBuilderOpen}
        defaultCategory={builderDefaultCategory}
        onCreate={handleCreateScenario}
      />
    </Page.Container>
  );
};
